import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import vlog from '../../javascript/vlog';

// V1-COMPAT: generic host HOC for the V2 profile SHOW pages ported into V1 (experts, companies,
// products, pr_profiles, media_profiles, media_outlets). V1 does not know V2 ids -- only its own
// record id, which is the same value the V2 PostgREST views carry back as `migrate_id` (see
// id/app/postgrest_api/id/apis/*_api.sql.erb). Before any of the copied show trees can render,
// this HOC resolves `entityMigrateId` to the V2 id and mounts the wrapped TopLevelShow with the id
// prop name that page's contract expects (expertId / companyId / productId / prProfileId /
// mediaProfileId / mediaOutletId -- `idPropName`, fixed per entity type by the thin wrapper files
// in this directory alongside `apiTable`/`idColumn`). Reuses with_v2_identity.jsx's retry timing: a
// same-transaction V1->V2 write can take a few seconds to land in the read replica/PostgREST cache
// the same way a brand-new signup's profile can.
//
// UNLIKE withV2Identity, this HOC BLOCKS. A show page has nothing to render at all without its
// entity -- there is no "logged-out-shaped degraded mode" the way the identity globals or the
// source directory have one -- so it renders nothing while resolving and a small plain-HTML
// notice on definitive failure (no row after retries, or a real API error). Never a crash, never
// a blank page with no explanation.
//
// Composition is withV2Identity(withV2Entity(TopLevelShow, config)) (see the four *_show_host.jsx
// files) -- identity resolution runs independently, outside this HOC, and writes
// globalThis.Qwoted.CURRENT_USER_ID; this HOC reads that global directly (rather than accept it as
// a prop) so `userId` -- which CompanyTopLevelShow/ProductTopLevelShow read as a signed-in check --
// is always the real V2 user id or nothing, never a V1 id standing in for one. ExpertTopLevelShow
// and PrProfileTopLevelShow don't declare a userId prop; the extra prop is harmless.
const PGRST_NO_ROWS_CODE = 'PGRST116';
const RETRY_DELAYS_MS = [1000, 3000, 6000];

// See withV2Identity.jsx for the full rationale: a backgrounded/discarded tab's in-flight fetch
// throws a native abort/network error that self-heals on remount and is not worth reporting.
const isTransientFetchError = (error) => {
  if (!error) return false;
  if (error.name === 'AbortError') return true;
  const message = typeof error.message === 'string' ? error.message : '';
  return /failed to fetch|load failed|network ?error|aborterror|the (operation|user) (was )?aborted/i.test(message);
};

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

function withV2Entity(WrappedComponent, {
  apiTable, idColumn, idPropName, idMatch = 'eq', narrowQuery = null,
}) {
  function V2EntityResolver(props) {
    const { entityMigrateId, ...rest } = props;
    // Resolver state is keyed by the `entityMigrateId` that produced it -- same pattern as
    // with_v2_identity.jsx's clearedFor/userId ownership gate. Without this, the render right
    // after `entityMigrateId` changes would still hold the PREVIOUS id's `status`/`v2Id` (the
    // reset happens in the passive effect below, which always runs after that render), so the
    // gate below checks the stored id against the current prop rather than trusting `status`
    // alone.
    const [state, setState] = useState({ status: 'loading', v2Id: null, entityMigrateId });

    useEffect(() => {
      let cancelled = false;

      const queryEntity = () => {
        const selected = globalThis.Qwoted.supabaseClient.from(apiTable).select('id');
        // `contains` is for media_outlets, whose id column is an ARRAY of every V1 publication id
        // that resolves to the outlet (merged_migrate_ids) rather than a single one -- see that
        // page's wrapper. Everything else matches a scalar column.
        const filtered = idMatch === 'contains'
          ? selected.contains(idColumn, [entityMigrateId])
          : selected.eq(idColumn, entityMigrateId);

        // `narrowQuery` adds whatever further predicate an entity needs to make the V1 id
        // unambiguous. Only media_profiles uses one so far -- V1's id sequences overlap between
        // tables, so a V1 Reporter id can also name a V1 PrUser, and both can reach the same V2
        // table. See that page's wrapper.
        const narrowed = narrowQuery ? narrowQuery(filtered) : filtered;

        // limit(1) before single(): V2 holds a small number of rows that share one V1 id
        // (accidental double-migrations -- same name, same content), and `single` answers an error
        // for them rather than picking one, which would burn the retry ladder below and end on the
        // unavailable notice for a record that does exist. Ordering makes the pick deterministic
        // rather than whatever the planner happens to return first. With at most one row left,
        // `single` still fails exactly when there is NO row, which is the retry signal.
        return narrowed.order('id', { ascending: true }).limit(1).single();
      };

      const resolveEntity = async () => {
        try {
          // Init is async and fired fire-and-forget from the layout. Await it so we don't race
          // ahead of `window.supabaseClient` being set; idempotent.
          if (window.initializeSupabaseClient) {
            await window.initializeSupabaseClient();
          }

          // Retry both PGRST116 ("no row" -- a same-transaction V1 write can take a few seconds to
          // surface) and a transient fetch abort, which usually self-heals on retry. Sequential
          // awaits are intentional: each retry must wait for the previous response.
          /* eslint-disable no-await-in-loop */
          let result = await queryEntity();
          let attempt = 0;
          while (
            !cancelled
            && attempt < RETRY_DELAYS_MS.length
            && (result.error?.code === PGRST_NO_ROWS_CODE || result.status === 0 || isTransientFetchError(result.error))
          ) {
            await sleep(RETRY_DELAYS_MS[attempt]);
            if (cancelled) return;
            result = await queryEntity();
            attempt += 1;
          }
          /* eslint-enable no-await-in-loop */
          if (cancelled) return;

          const { data, error } = result;

          if (!error && data?.id != null) {
            setState({ status: 'ready', v2Id: data.id, entityMigrateId });
            return;
          }

          vlog.error(`Failed to resolve V2 ${apiTable} entity`, { entityMigrateId, error });
          // eslint-disable-next-line no-console -- deliberately survives vlog.disableAll() in production
          console.warn(`[V1-COMPAT] Failed to resolve V2 ${apiTable} entity; showing the unavailable notice.`, {
            entityMigrateId,
            error,
          });
          window.Rollbar?.error(`V2 ${apiTable} entity resolution failed`, { entityMigrateId, error });
          setState({ status: 'error', v2Id: null, entityMigrateId });
        } catch (err) {
          if (cancelled) return;
          vlog.error(`Failed to resolve V2 ${apiTable} entity`, err);
          // eslint-disable-next-line no-console -- deliberately survives vlog.disableAll() in production
          console.warn(`[V1-COMPAT] Failed to resolve V2 ${apiTable} entity; showing the unavailable notice.`, err);
          window.Rollbar?.error(`V2 ${apiTable} entity resolution failed`, err);
          setState({ status: 'error', v2Id: null, entityMigrateId });
        }
      };

      setState({ status: 'loading', v2Id: null, entityMigrateId });
      resolveEntity();

      return () => {
        cancelled = true;
      };
    }, [entityMigrateId]);

    // Stale-frame guard: `state` was computed for whichever `entityMigrateId` was current when
    // its resolution started/settled. If the prop has since changed, the passive effect above
    // hasn't reset `state` for the new id yet on this render (effects always run AFTER the
    // render they were scheduled by) -- render loading rather than let the PREVIOUS entity's
    // `v2Id` (or its error notice) render for one frame under the new props.
    if (state.status === 'loading' || state.entityMigrateId !== entityMigrateId) return null;

    if (state.status === 'error') {
      return (
        <div className="text-center text-muted py-5" data-testid="v2-entity-unavailable">
          This profile view is temporarily unavailable.
        </div>
      );
    }

    // The current viewer's own V2 user id, if identity has already resolved -- see the module
    // comment above.
    const userId = globalThis.Qwoted?.CURRENT_USER_ID;

    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <WrappedComponent {...rest} {...{ [idPropName]: state.v2Id }} userId={userId} />
    );
  }

  V2EntityResolver.propTypes = {
    entityMigrateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  };

  return V2EntityResolver;
}

export default withV2Entity;
